import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  noIndex = false,
  jsonLd,
}: SEOHeadProps) {
  const jsonLdScriptsRef = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    document.title = title;

    const setMeta = (nameOrProp: string, content: string, useProperty = false) => {
      const attr = useProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${nameOrProp}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, nameOrProp);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.rel = rel;
        document.head.appendChild(el);
      }
      (el as HTMLLinkElement).href = href;
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonical) setLink('canonical', canonical);
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    setMeta('og:title', ogTitle ?? title, true);
    setMeta('og:description', ogDescription ?? description, true);
    if (ogUrl) setMeta('og:url', ogUrl, true);
    setMeta('og:type', 'website', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle ?? title);
    setMeta('twitter:description', ogDescription ?? description);
    if (ogImage) setMeta('twitter:image', ogImage);

    // JSON-LD — remove previously injected scripts by this component
    jsonLdScriptsRef.current.forEach((s) => s.remove());
    jsonLdScriptsRef.current = [];

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
        jsonLdScriptsRef.current.push(script);
      });
    }

    return () => {
      jsonLdScriptsRef.current.forEach((s) => s.remove());
      jsonLdScriptsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogUrl, ogImage, noIndex]);

  return null;
}
